import { useState } from 'react';
import { DataTableColumn, DataTableSortStatus } from 'mantine-datatable';
import { BaseRecord, SortableColumn } from './types';
import { Table, TableProps } from './Table';
import { useTable, TableMode } from './hooks/useTable';
import { TABLE_VARIANTS, TableVariant } from './tableVariants';

type SimpleTableProps<T extends BaseRecord> = {
  resource: string;
  mode?: TableMode;
  columns: SortableColumn<T>[];
  filters?: Record<string, string>;
  searchAttributes?: string[];
  pageSize?: number;
  onRowClick?: (record: T) => void;
  title?: string;
  titleIcon?: React.ReactNode;
  onTitleClick?: () => void;
  primaryButton?: {
    label: string;
    onClick: () => void;
    variant?: 'default' | 'primary';
  };
  HeaderRow?: React.ReactNode;
  showCount?: boolean;
  titleSuffix?: React.ReactNode;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  striped?: boolean;
  variant?: TableVariant;
  sortable?: boolean;
  externalSearchTerm?: string;
  tableProps?: Partial<
    Omit<TableProps<T>, 'data' | 'isLoading' | 'pagination' | 'totalRecords' | 'columns'>
  >;
  filterPredicate?: (item: T, search: string) => boolean;
  // Reset pagination when this changes
  paginationDependencies?: unknown;
  // Use local state for pagination instead of URL params (useful for multi-table pages)
  useLocalPagination?: boolean;
  initialSort?: DataTableSortStatus<T>;
};

export function SimpleTable<T extends BaseRecord>({
  resource,
  mode = 'client',
  columns,
  filters,
  searchAttributes,
  pageSize = 10,
  showCount = false,
  titleSuffix,
  onRowClick,
  title,
  titleIcon,
  onTitleClick,
  primaryButton,
  HeaderRow,
  enableSearch = true,
  searchPlaceholder,
  striped,
  variant = 'default',
  sortable = true,
  externalSearchTerm,
  tableProps,
  paginationDependencies,
  filterPredicate,
  useLocalPagination = false,
  initialSort,
}: SimpleTableProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');

  const variantConfig = TABLE_VARIANTS[variant];
  const resolvedStriped = striped ?? variantConfig.striped ?? true;

  const needsColumnOverrides = !!variantConfig.columnTitleStyle || !sortable;
  const displayColumns = needsColumnOverrides
    ? columns.map(column => ({
        ...column,
        ...(variantConfig.columnTitleStyle && { titleStyle: variantConfig.columnTitleStyle }),
        ...(sortable ? null : { sortable: false, sortIconType: undefined }),
      }))
    : columns;

  const autoFilterPredicate = filterPredicate ?? buildFilterPredicate(searchAttributes, columns);

  const showSearch = mode === 'client' && enableSearch;

  const activeSearchTerm = showSearch ? searchTerm : (externalSearchTerm ?? '');
  const activeFilterPredicate =
    showSearch || externalSearchTerm !== undefined ? autoFilterPredicate : undefined;

  const tableData = useTable<T>({
    resource,
    mode,
    columns,
    searchTerm: activeSearchTerm,
    filterPredicate: activeFilterPredicate,
    paginationDependencies,
    filters,
    pageSize,
    useLocalPagination,
    initialSort,
  });

  return (
    <Table<T>
      title={title}
      titleIcon={titleIcon}
      onTitleClick={onTitleClick}
      columns={displayColumns}
      data={tableData.data}
      isLoading={tableData.isLoading}
      onRowClick={onRowClick || (() => {})}
      searchTerm={showSearch ? searchTerm : undefined}
      setSearchTerm={showSearch ? setSearchTerm : undefined}
      searchPlaceholder={searchPlaceholder}
      pagination={tableData.pagination}
      totalRecords={tableData.totalRecords}
      sortStatus={sortable ? tableData.sortStatus : undefined}
      onSortStatusChange={sortable ? tableData.onSortStatusChange : undefined}
      primaryButton={primaryButton}
      HeaderRow={HeaderRow}
      showCount={showCount}
      titleSuffix={titleSuffix}
      striped={resolvedStriped}
      {...variantConfig.tableProps}
      {...tableProps}
    />
  );
}

function buildFilterPredicate<T extends BaseRecord>(
  searchAttributes: string[] | undefined,
  columns: DataTableColumn<T>[],
) {
  const searchableAccessors =
    searchAttributes ??
    columns
      .filter(col => col.accessor && col.accessor !== 'actions')
      .map(col => col.accessor as keyof T);

  return (item: T, search: string): boolean => {
    if (!search.trim()) return true;

    const searchLower = search.toLowerCase();

    return searchableAccessors.some(accessor => {
      const value = item[accessor as keyof T];

      if (typeof value === 'string') {
        return value.toLowerCase().includes(searchLower);
      }

      if (typeof value === 'number') {
        return value.toString().includes(search);
      }

      if (value && typeof value === 'object' && 'toLocaleDateString' in value) {
        return (value as Date).toLocaleDateString().toLowerCase().includes(searchLower);
      }

      if (typeof value === 'object' && value !== null) {
        try {
          return JSON.stringify(value).toLowerCase().includes(searchLower);
        } catch {
          return false;
        }
      }

      return String(value).toLowerCase().includes(searchLower);
    });
  };
}
